import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { db } from '../Config/Config';
import { collection, addDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function Career({ careerData = {} }) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        position: "",
        message: "",
    });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const storage = getStorage();

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const disableMotion = prefersReducedMotion || isMobile;


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage('');
                setMessageType('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let resumeURL = "";

            if (file) {
                const storageRef = ref(storage, `career-uploads/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                resumeURL = await getDownloadURL(storageRef);
            }

            await addDoc(collection(db, "career-apply"), {
                ...formData,
                resumeURL,
                createdAt: new Date(),
                status: "pending",
            });

            setFormData({ name: "", email: "", phone: "", position: "", message: "" });
            setFile(null);
            setMessage('Application submitted successfully! We\'ll review it and get back to you soon.');
            setMessageType('success');
        } catch (err) {
            console.error("Error submitting:", err);
            setMessage('Error submitting application. Please try again.');
            setMessageType('error');
        }

        setLoading(false);
    };

    const description = careerData.description || "";
    const points = careerData.points || [];

    return (
        <section id="careers" className="relative min-h-screen py-20">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
                <div className="flex flex-col md:flex-row  gap-8 md:gap-12 items-stretch">
                    {/* Left Column */}
                    <div className=''>
                        <motion.div
                            className="mb-8"
                            initial={disableMotion ? false : { opacity: 0, y: 20 }}
                            whileInView={disableMotion ? {} : { opacity: 1, y: 0 }}
                            transition={{ duration: disableMotion ? 0 : 0.4 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
                                <span className="text-secondary">Join</span> Our Team
                            </h2>
                        </motion.div>

                        <motion.div
                            className="space-y-6"
                            initial={disableMotion ? false : { opacity: 0, y: 20 }}
                            whileInView={disableMotion ? {} : { opacity: 1, y: 0 }}
                            transition={{ duration: disableMotion ? 0 : 0.4, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <p className="text-lg text-slate-200 leading-relaxed">
                                {description}
                            </p>

                            {/* Why Join Us Section */}
                            {points.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="text-xl font-heading font-semibold text-white">
                                        Why Join Us?
                                    </h3>
                                    <ul className="space-y-3">
                                        {points.map((point, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg
                                                    className="w-6 h-6 text-secondary mt-0.5 mr-3 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M5 13l4 4L19 7"
                                                    ></path>
                                                </svg>
                                                <span className="text-slate-300">{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </motion.div>
                    </div>

                    {/* Right Column (Form) */}
                    <div className='lg:col-span-2'>
                        <motion.div
                            initial={disableMotion ? false : { opacity: 0, y: 20 }}
                            whileInView={disableMotion ? {} : { opacity: 1, y: 0 }}
                            transition={{ duration: disableMotion ? 0 : 0.5 }}
                            viewport={{ once: true }}
                            className="w-full bg-white/5 backdrop-blur-md rounded-xl p-4 md:p-6 lg:p-8 lg:w-[700px] border border-white/10"
                        >
                            <h3 className="text-2xl font-heading font-bold text-white mb-2">
                                Apply Now
                            </h3>
                            <p className="text-slate-300">
                                Fill out the form below and we'll get back to you soon.
                            </p>

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                                            Full Name <span className="text-secondary">*</span>
                                        </label>
                                        <input
                                            id="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            name="name"
                                            type="text"
                                            required
                                            className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                                        />
                                    </div>
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                                            Email <span className="text-secondary">*</span>
                                        </label>
                                        <input
                                            id="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            name="email"
                                            type="email"
                                            required
                                            className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                                            Phone Number
                                        </label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="w-full md:w-1/2">
                                        <label htmlFor="position" className="block text-sm font-medium text-white mb-2">
                                            Position
                                        </label>
                                        <input
                                            id="position"
                                            value={formData.position}
                                            onChange={handleChange}
                                            name="position"
                                            type="text"
                                            className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-secondary focus:border-secondary transition text-black placeholder-slate-400"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="resume" className="block text-sm font-medium text-white mb-2">
                                        Upload Resume/Portfolio
                                    </label>
                                    <input
                                        id="resume"
                                        name="resume"
                                        type="file"
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx"
                                        className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                                                    file:text-sm file:bg-secondary file:text-primary-dark hover:file:bg-secondary-dark
                                                    text-sm text-black rounded-lg cursor-pointer
                                                    file:cursor-pointer file:transition-colors
                                                    bg-white border border-white/20 focus:outline-secondary focus:border-secondary"
                                    />
                                    <p className="mt-1 text-xs text-slate-400">
                                        Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                                        Cover Letter <span className="text-slate-400">(Optional)</span>
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-4 py-2.5 bg-white border border-white/20 rounded-lg focus:outline-none focus:border-secondary transition text-black placeholder-slate-400 resize-none"
                                        placeholder="Tell us why you'd be a great fit..."
                                    />
                                </div>

                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        className="w-full bg-secondary hover:bg-secondary-dark text-primary-dark font-semibold py-3.5 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Submit Application"}
                                    </button>

                                    {message && (
                                        <div
                                            className={`mt-4 p-4 rounded-lg border ${messageType === 'success'
                                                ? 'bg-green-500/10 border-green-500/20 text-green-300'
                                                : 'bg-red-500/10 border-red-500/20 text-red-300'
                                                }`}
                                        >
                                            <p className="text-sm">{message}</p>
                                        </div>
                                    )}

                                    <p className="mt-3 text-xs text-center text-slate-400">
                                        We'll review your application and get back to you within 3 business days.
                                    </p>
                                </div>
                            </form>
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}
